import { ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatSelectionListChange } from '@angular/material/list';
import { getHoveredFieldId } from '@next/arable-lib/geo';
import { Store, select } from '@ngrx/store';
import { BehaviorSubject, combineLatest, ReplaySubject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import {
  DeselectInteractionFeatures,
  InteractionFeatureToggle,
} from 'src/app/geo/store/actions/feature.actions';
import {
  getSelectedFeaturesIdsAsNumber,
  hasSelectedFeatures,
} from 'src/app/geo/store/selectors/feature.selectors';
import { SiteState, CropField, DetailedCropField } from 'src/app/shared/models';
import { ToggleCropFields, SetSearch } from 'src/app/store/actions';
import { hasSearch, hasAllCropFieldsSelected } from 'src/app/store/selectors';

import { SetFilteredCropFieldIds } from '../../store/actions/crop-field.actions';
import { getDetailedCropFields } from '../../store/selectors/crop-field.selectors';

@Component({
  selector: 'app-cropfield-list',
  templateUrl: './cropfield-list.component.html',
  styleUrls: ['./cropfield-list.component.scss'],
  standalone: false,
})
export class CropFieldListComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly ref = inject(ChangeDetectorRef);

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  search$ = this.store.pipe(select(hasSearch));
  private filteredCropFields$ = new BehaviorSubject<DetailedCropField[]>([]);
  private detailedCropFields: DetailedCropField[] = [];
  cropFields$ = this.store.pipe(select(getDetailedCropFields));
  hasSelectedField$ = this.store.pipe(select(hasSelectedFeatures));
  isAllSelected$ = this.store.pipe(select(hasAllCropFieldsSelected));
  selectedCropFieldIds: number[] = [];
  selectedCropFieldIds$ = this.store.pipe(select(getSelectedFeaturesIdsAsNumber));
  selectedCropFields$ = combineLatest([
    this.filteredCropFields$,
    this.cropFields$,
    this.search$,
    this.selectedCropFieldIds$,
  ]).pipe(
    map(([filteredCropFields, cropFields, search, selectedCropFieldsIds]) =>
      (search ? filteredCropFields : cropFields).filter(
        (cropField) => selectedCropFieldsIds.indexOf(cropField.id) !== -1,
      ),
    ),
  );
  unselectedCropFields$ = combineLatest([
    this.filteredCropFields$,
    this.cropFields$,
    this.search$,
    this.selectedCropFieldIds$,
  ]).pipe(
    map(([filteredCropFields, cropFields, search, selectedCropFieldsIds]) =>
      (search ? filteredCropFields : cropFields).filter(
        (cropField) => selectedCropFieldsIds.indexOf(cropField.id) === -1,
      ),
    ),
  );
  hoveredFieldId: number = null;

  ngOnInit() {
    this.selectedCropFieldIds$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((selectedCropFieldIds) => {
        setTimeout(() => {
          this.selectedCropFieldIds = selectedCropFieldIds;
          this.ref.detectChanges();
        }, 100);
      });
    this.cropFields$.pipe(takeUntil(this.destroyed$)).subscribe((cropFields) => {
      this.detailedCropFields = cropFields;
    });

    this.store
      .pipe(select(getHoveredFieldId), takeUntil(this.destroyed$))
      .subscribe((hoveredFieldId) => {
        this.hoveredFieldId = hoveredFieldId;
      });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  changeSelectedCropField(change: MatSelectionListChange) {
    if (change.options && change.options.length > 0) {
      this.store.dispatch(new InteractionFeatureToggle(change.options[0].value as number));
    }
  }

  masterToggle() {
    const filteredFields = this.filteredCropFields$.getValue() as CropField[];
    this.store.dispatch(
      new ToggleCropFields(filteredFields.length ? filteredFields : this.detailedCropFields),
    );
  }

  setFilteredCropFields(filtered: DetailedCropField[]) {
    const filteredIds = filtered.map((cropField) => cropField.id);
    this.store.dispatch(new SetFilteredCropFieldIds(filteredIds));
    this.store.dispatch(new DeselectInteractionFeatures());
    this.filteredCropFields$.next(filtered);
  }

  closeSearch() {
    this.store.dispatch(new SetSearch(false));
  }

  trackByFn(index: number) {
    return index;
  }
}
